import { useState, useCallback, useEffect } from 'react';
import { Routes, Route, NavLink, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { DashboardPage } from './pages/DashboardPage';
import { MachinePage } from './pages/MachinePage';
import { EventsPage } from './pages/EventsPage';
import { SessionsPage } from './pages/SessionsPage';
import { ReportsPage } from './pages/ReportsPage';
import { LoginPage } from './pages/LoginPage';
import { UsersPage } from './pages/UsersPage';
import { RefundsPage } from './pages/RefundsPage';
import { AccountPage } from './pages/AccountPage';
import { AdminKeyPage } from './pages/AdminKeyPage';
import { AlertsPage } from './pages/AlertsPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { CasesPage } from './pages/CasesPage';
import { CaseDetailPage } from './pages/CaseDetailPage';
import { PricingPage } from './pages/PricingPage';
import { ArchivePage } from './pages/ArchivePage';
import { logout, getVersion, checkAuth, type AuthUser } from './api/client';

const PUBLIC_PATHS = ['/login'];

function usePageTitle(): string {
  const { pathname } = useLocation();
  if (pathname.startsWith('/machines/')) return 'Machine Detail';
  if (pathname === '/sessions') return 'Sessions';
  if (pathname === '/events') return 'Events';
  if (pathname === '/alerts') return 'Alerts';
  if (pathname === '/refunds') return 'Refunds';
  if (pathname === '/reports') return 'Reports';
  if (pathname === '/feedback') return 'Reviews';
  if (pathname.startsWith('/cases')) return 'Complaints';
  if (pathname === '/pricing') return 'Pricing';
  if (pathname === '/users') return 'Users';
  if (pathname === '/account') return 'Account';
  if (pathname === '/admin-key') return 'Admin key';
  if (pathname === '/archive') return 'Archive';
  if (pathname === '/login') return 'Login';
  return 'Dashboard';
}

export function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [serverVersion, setServerVersion] = useState<string>('');
  const pageTitle = usePageTitle();
  const location = useLocation();
  const navigate = useNavigate();

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);

  useEffect(() => {
    checkAuth()
      .then((u) => {
        setUser(u);
        setAuthChecked(true);
      })
      .catch(() => {
        setUser(null);
        setAuthChecked(true);
      });
  }, []);

  useEffect(() => {
    getVersion()
      .then((v) => setServerVersion(v.server))
      .catch(() => {});
  }, []);

  async function handleLogout() {
    try { await logout(); } catch { /* ignore */ }
    setUser(null);
    navigate('/login', { replace: true });
  }

  if (!authChecked) return null;

  if (!user && !PUBLIC_PATHS.includes(location.pathname)) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={<LoginPage onSuccess={(u) => setUser(u)} />}
      />

      <Route
        path="*"
        element={
          <div className="app-layout">
            <div
              className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
              onClick={closeSidebar}
            />

            <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
              <div className="sidebar-brand">
                <div className="brand-icon">M</div>
                <h1>MyCopy</h1>
              </div>
              <nav className="sidebar-nav" onClick={closeSidebar}>
                <NavLink to="/" end>
                  <span className="nav-icon">&#9633;</span>
                  Dashboard
                </NavLink>
                <NavLink to="/sessions">
                  <span className="nav-icon">&#9783;</span>
                  Sessions
                </NavLink>
                <NavLink to="/events">
                  <span className="nav-icon">&#9776;</span>
                  Events
                </NavLink>
                <NavLink to="/reports">
                  <span className="nav-icon">&#128202;</span>
                  Reports
                </NavLink>
                <NavLink to="/feedback">
                  <span className="nav-icon">&#9734;</span>
                  Reviews
                </NavLink>
                {/* Тикеты жалоб: только superadmin и те, кому дали галочку —
                    там почта клиентов и кнопка возврата денег. */}
                {(user?.role === 'superadmin' || user?.canHandleCases) && (
                  <NavLink to="/cases">
                    <span className="nav-icon">&#9993;</span>
                    Complaints
                  </NavLink>
                )}
                <NavLink to="/alerts">
                  <span className="nav-icon">&#9888;</span>
                  Alerts
                </NavLink>
                {/* Оператор только смотрит: очередь возвратов — действие, ему её не показываем. */}
                {user?.role !== 'operator' && (
                  <NavLink to="/refunds">
                    <span className="nav-icon">&#8617;</span>
                    Refunds
                  </NavLink>
                )}
                {/* Цены меняет только владелец. */}
                {user?.role === 'superadmin' && (
                  <NavLink to="/pricing">
                    <span className="nav-icon">&#128176;</span>
                    Pricing
                  </NavLink>
                )}
                {user?.role === 'superadmin' && (
                  <NavLink to="/users">
                    <span className="nav-icon">&#9733;</span>
                    Users
                  </NavLink>
                )}
                {user?.role === 'superadmin' && (
                  <NavLink to="/admin-key">
                    <span className="nav-icon">&#128273;</span>
                    Admin key
                  </NavLink>
                )}
                {user?.role === 'superadmin' && (
                  <NavLink to="/archive">
                    <span className="nav-icon">&#128451;</span>
                    Archive
                  </NavLink>
                )}
                <NavLink to="/account">
                  <span className="nav-icon">&#9678;</span>
                  Account
                </NavLink>
              </nav>
              {serverVersion && (
                <div className="sidebar-footer">v{serverVersion}</div>
              )}
            </aside>

            <div className="main-content">
              <header className="header">
                <div className="flex items-center gap-2">
                  <button
                    className="menu-toggle"
                    onClick={toggleSidebar}
                    aria-label="Toggle menu"
                  >
                    &#9776;
                  </button>
                  <span className="header-title">{pageTitle}</span>
                </div>
                <div className="header-actions">
                  {user && (
                    <span className="text-muted" style={{ fontSize: '0.8rem', marginRight: 8 }}>
                      {user.username}{user.role === 'superadmin' ? ' ★' : ''}
                    </span>
                  )}
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleLogout}
                    style={{ fontSize: '0.78rem' }}
                  >
                    Logout
                  </button>
                </div>
              </header>

              <main className="page-content">
                <Routes>
                  <Route path="/" element={<DashboardPage role={user?.role} />} />
                  <Route path="/machines/:id" element={<MachinePage canEdit={user?.role !== 'operator'} canDelete={user?.role === 'superadmin'} />} />
                  <Route path="/sessions" element={<SessionsPage role={user?.role} />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/feedback" element={<FeedbackPage />} />
                  <Route path="/cases" element={(user?.role === 'superadmin' || user?.canHandleCases) ? <CasesPage /> : <Navigate to="/" replace />} />
                  <Route path="/cases/:id" element={(user?.role === 'superadmin' || user?.canHandleCases) ? <CaseDetailPage user={user} /> : <Navigate to="/" replace />} />
                  <Route path="/alerts" element={<AlertsPage canAct={user?.role !== 'operator'} />} />
                  <Route
                    path="/refunds"
                    element={user?.role !== 'operator' ? <RefundsPage /> : <Navigate to="/" replace />}
                  />
                  <Route
                    path="/pricing"
                    element={user?.role === 'superadmin' ? <PricingPage /> : <Navigate to="/" replace />}
                  />
                  <Route path="/account" element={<AccountPage user={user} />} />
                  <Route
                    path="/admin-key"
                    element={
                      user?.role === 'superadmin' ? (
                        <AdminKeyPage />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      user?.role === 'superadmin' ? (
                        <UsersPage currentUserId={user.id} />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  <Route
                    path="/archive"
                    element={user?.role === 'superadmin' ? <ArchivePage /> : <Navigate to="/" replace />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
            </div>
          </div>
        }
      />
    </Routes>
  );
}
